import React, { useState, useEffect } from "react";
import "./css/company_management.scss";

import Section from "../ui/section";
import SystemAdminService from "../../services/system_admin_service";
import ErrorMessages from "../ui/error_messages";
import { company_metadata } from "../../schema/system_admin/company_metadata";
import moment from "moment-timezone";
import { SUBSCRIPTION_STATUSES } from "../../schema/system_admin/company_details";
import EditCompany from "./edit_company";
import AddCompany from "./add_company";
import { handleErrorMessages } from "../../services/utils";
import config from "../../config/config.js";

export default function CompanyManagement() {
  const [activeSection, setActiveSection] = useState("MANAGEMENT");
  const [errorMessages, setErrorMessages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editCompanyId, setEditCompanyId] = useState(0);
  const [seedingId, setSeedingId] = useState(0);
  const [busyId, setBusyId] = useState(0);
  const [showArchived, setShowArchived] = useState(false);

  function runCompanyAction(id, action) {
    setErrorMessages([]);
    setBusyId(id);
    return action()
      .then(() => loadCompanies())
      .catch(error => handleErrorMessages(error, setErrorMessages))
      .finally(() => setBusyId(0));
  }

  function archiveCompany(company) {
    if (
      !window.confirm(
        `Archive "${company.name || "Company " + company.id}"?\n\n` +
          "Its subscription is turned off and it is hidden from this list. " +
          "All users and loan files are KEPT, and you can Restore it any time " +
          "(Show Archived: Yes)."
      )
    ) {
      return;
    }
    runCompanyAction(company.id, () =>
      SystemAdminService.archiveCompany(company.id)
    );
  }

  function restoreCompany(company) {
    runCompanyAction(company.id, () =>
      SystemAdminService.restoreCompany(company.id)
    );
  }

  function deleteCompany(company) {
    const label = company.name || "Company " + company.id;
    const typed = window.prompt(
      `PERMANENTLY DELETE "${label}"?\n\n` +
        "This cancels its Stripe subscription and deletes every user and every " +
        "loan file in the company. It CANNOT be undone. (To keep the data, use " +
        "Archive instead.)\n\nType DELETE to confirm:"
    );
    if (typed !== "DELETE") {
      if (typed !== null) {
        window.alert("Not deleted. You must type DELETE (all capitals).");
      }
      return;
    }
    runCompanyAction(company.id, () =>
      SystemAdminService.deleteCompany(company.id).then(result => {
        window.alert(
          `Deleted "${label}": ${result.deletedUsers} user(s) and ` +
            `${result.deletedDocuments} loan file(s).`
        );
      })
    );
  }

  function seedCompany(id) {
    if (
      !window.confirm(
        "Regenerate demo loan + lost-scenario data for this company?\n\n" +
          "This replaces the company's existing demo dashboard data (VIEW + VISION), " +
          "weighted so Today, Month, Quarter and YTD all show activity."
      )
    ) {
      return;
    }
    setSeedingId(id);
    SystemAdminService.seedDemoData(id)
      .then(({ loans, lost }) => {
        const loanCount = loans && loans.loansSeeded != null ? loans.loansSeeded : "?";
        const lostCount = lost && lost.lostSeeded != null ? lost.lostSeeded : "?";
        window.alert(
          `Demo data regenerated: ${loanCount} loans and ${lostCount} lost scenarios.\n` +
            "Reload the VIEW / VISION dashboard to see it."
        );
      })
      .catch(error => handleErrorMessages(error, setErrorMessages))
      .finally(() => setSeedingId(0));
  }

  function loadCompanies() {
    SystemAdminService.getCompanies()
      .then(metadatas =>
        setCompanies(
          metadatas.map(metadata => ({
            ...company_metadata,
            ...metadata
          }))
        )
      )
      .catch(error => handleErrorMessages(error, setErrorMessages));
  }

  function editCompany(id) {
    setEditCompanyId(id);
    setActiveSection("EDIT_COMPANY");
  }

  useEffect(() => {
    if (activeSection === "MANAGEMENT") {
      loadCompanies();
    }
  }, [activeSection]);

  return (
    <div className="company-management">
      {activeSection === "MANAGEMENT" && (
        <Section name="Company Management" className="companies">
          <ErrorMessages messages={errorMessages} />
          <div>
            <button
              className="button"
              onClick={() => setActiveSection("ADD_COMPANY")}
            >
              Add New Company
            </button>
            <label className="show-archived">
              Show Archived
              <select
                value={showArchived ? "yes" : "no"}
                onChange={e => setShowArchived(e.target.value === "yes")}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
          </div>
          <div>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company Name</th>
                  <th>Company Email</th>
                  <th>Subscription Active</th>
                  <th>Subscription End Date</th>
                  <th>Subscription Status</th>
                  <th>Current Seats (Limit)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {companies
                  .filter(company => showArchived || !company.archived)
                  .map(company => (
                  <tr
                    key={company.id}
                    className={company.archived ? "archived-company" : ""}
                  >
                    <td>{company.id}</td>
                    <td>{company.name}</td>
                    <td>{company.email}</td>
                    <td>{company.subscription_active ? "Yes" : "No"}</td>
                    <td>
                      {company.subscription_end_date > 0 &&
                        moment(company.subscription_end_date)
                          .tz(moment.tz.guess())
                          .format("MM/DD/YYYY hh:mm A")}
                    </td>
                    <td>
                      {company.archived
                        ? "Archived"
                        : SUBSCRIPTION_STATUSES[company.subscription_status]}
                    </td>
                    <td>
                      {company.current_seat_amount} (
                      {company.seat_limit >= 999
                        ? "Unlimited"
                        : company.seat_limit}
                      )
                    </td>
                    <td>
                      <button
                        className="button"
                        onClick={() => editCompany(company.id)}
                      >
                        Edit
                      </button>
                      {company.archived ? (
                        <button
                          className="button"
                          onClick={() => restoreCompany(company)}
                          disabled={busyId === company.id}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          className="button"
                          onClick={() => archiveCompany(company)}
                          disabled={busyId === company.id}
                        >
                          Archive
                        </button>
                      )}
                      <button
                        className="button"
                        onClick={() => deleteCompany(company)}
                        disabled={busyId === company.id}
                      >
                        Delete
                      </button>
                      {/* Demo dashboard seeding is Dev/Demo only: on Production it
                          would put fake loans into a real company's dashboard. */}
                      {config.enableDashboard && (
                        <button
                          className="button"
                          onClick={() => seedCompany(company.id)}
                          disabled={seedingId === company.id}
                        >
                          {seedingId === company.id ? "Seeding…" : "Seed Demo Data"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {activeSection === "EDIT_COMPANY" && (
        <EditCompany
          companyId={editCompanyId}
          onExit={() => {
            setActiveSection("MANAGEMENT");
          }}
        />
      )}
      {activeSection === "ADD_COMPANY" && (
        <AddCompany
          onExit={() => {
            setActiveSection("MANAGEMENT");
          }}
        />
      )}
    </div>
  );
}
